import { create } from 'zustand';
import { db } from '../lib/supabase';
import type { User, Achievement, UserAchievement } from '../types';
import { LEVELS, ACHIEVEMENTS, XP_REWARDS } from '../types';
import { notifyAchievement } from '../lib/native/haptics';
import { trackDailyGoalMetOnce, trackEvent } from '../lib/analytics';
import { effectiveStreak } from '../lib/trial';
import { localDateString } from '../lib/dates';

interface GamificationState {
  // User stats
  xp: number;
  level: number;
  levelName: string;
  levelIcon: string;
  xpToNextLevel: number;
  xpProgress: number; // 0-100 percentage

  // Streaks
  currentStreak: number;
  longestStreak: number;
  streakLastDate: string | null;

  // Session stats
  sessionCorrectStreak: number;
  questionsAnsweredToday: number;
  dailyGoalCompleted: boolean;
  dailyGoal: number;

  // Achievements
  earnedAchievements: UserAchievement[];
  newAchievement: Achievement | null;

  // Loading
  isLoading: boolean;

  // Actions
  loadUserStats: (userId: string) => Promise<void>;
  setDailyGoal: (goal: number) => void;
  awardXP: (userId: string, amount: number, reason: string) => Promise<void>;
  recordAnswer: (correct: boolean) => void;
  checkAndUpdateStreak: (userId: string) => Promise<void>;
  checkAchievements: (user: User) => Promise<Achievement[]>;
  dismissNewAchievement: () => void;
  resetSession: () => void;
}

// Helper function to calculate level from XP
function calculateLevel(xp: number): { level: number; name: string; icon: string } {
  for (let i = LEVELS.length - 1; i >= 0; i--) {
    if (xp >= LEVELS[i].xp_required) {
      return {
        level: LEVELS[i].level,
        name: LEVELS[i].name,
        icon: LEVELS[i].icon,
      };
    }
  }
  return { level: 1, name: 'Seedling', icon: '🌱' };
}

// Helper to calculate XP progress to next level
function calculateXPProgress(xp: number): { xpToNextLevel: number; progress: number } {
  const currentLevelInfo = calculateLevel(xp);
  const nextLevel = LEVELS.find((l) => l.level === currentLevelInfo.level + 1);

  if (!nextLevel) {
    return { xpToNextLevel: 0, progress: 100 };
  }

  const currentLevelXP = LEVELS.find((l) => l.level === currentLevelInfo.level)?.xp_required || 0;
  const xpInCurrentLevel = xp - currentLevelXP;
  const xpNeededForNext = nextLevel.xp_required - currentLevelXP;
  const progress = Math.round((xpInCurrentLevel / xpNeededForNext) * 100);

  return {
    xpToNextLevel: nextLevel.xp_required - xp,
    progress: Math.min(progress, 100),
  };
}

// Check if today is a new day compared to last activity
function isNewDay(lastDate: string | null): boolean {
  if (!lastDate) return true;
  const today = new Date().toDateString();
  const last = new Date(lastDate).toDateString();
  return today !== last;
}

// Check if streak should continue (was active yesterday)
function isStreakContinued(lastDate: string | null): boolean {
  if (!lastDate) return false;
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const lastActivity = new Date(lastDate);
  return lastActivity.toDateString() === yesterday.toDateString() ||
         lastActivity.toDateString() === new Date().toDateString();
}

export const useGamificationStore = create<GamificationState>((set, get) => ({
  // Initial state
  xp: 0,
  level: 1,
  levelName: 'Seedling',
  levelIcon: '🌱',
  xpToNextLevel: 100,
  xpProgress: 0,

  currentStreak: 0,
  longestStreak: 0,
  streakLastDate: null,

  sessionCorrectStreak: 0,
  questionsAnsweredToday: 0,
  dailyGoalCompleted: false,
  dailyGoal: 10,

  earnedAchievements: [],
  newAchievement: null,

  isLoading: false,

  loadUserStats: async (userId: string) => {
    set({ isLoading: true });
    try {
      // Load user directly from users table
      const user = await db.users.get(userId);

      let achievements: UserAchievement[] = [];
      try {
        const rawAchievements = await db.achievements.getUserAchievements(userId);
        achievements = rawAchievements.map((a, index) => ({
          id: `${a.user_id}-${a.achievement_id}-${index}`,
          user_id: a.user_id,
          achievement_id: a.achievement_id,
          earned_at: a.earned_at,
        }));
      } catch {
        // Achievements table might not exist yet
      }

      const levelInfo = calculateLevel(user.xp || 0);
      const progressInfo = calculateXPProgress(user.xp || 0);

      // Check if it's a new day — but use UTC to match the server's CURRENT_DATE.
      // Server (Postgres) stores last_activity_date in UTC via CURRENT_DATE, so
      // the client MUST compare in UTC too. Previously we used local timezone,
      // which caused the daily counter to reset incorrectly for users outside
      // UTC (and, worse, wrote 0 back to the DB on every dashboard load).
      //
      // We no longer reset the DB from the client. The server's submit_answer
      // function already resets questions_answered_today to 1 when it detects
      // a new UTC day on the next submission. Until then, we just display 0
      // locally without mutating the DB.
      const todayUTC = localDateString(); // learner's local day (server uses users.timezone, migration 81)
      const lastActivityDate = user.last_activity_date
        ? String(user.last_activity_date).substring(0, 10)
        : null;

      const isNewDay = lastActivityDate !== todayUTC;
      const questionsToday = isNewDay ? 0 : (user.questions_answered_today || 0);
      const dailyGoal = user.daily_goal || 10;

      // Stats reload after every answer, so the crossing is observed here.
      // The helper dedupes to one funnel event per UTC day.
      if (questionsToday >= dailyGoal && questionsToday > 0) {
        trackDailyGoalMetOnce(todayUTC);
      }

      set({
        xp: user.xp || 0,
        level: levelInfo.level,
        levelName: levelInfo.name,
        levelIcon: levelInfo.icon,
        xpToNextLevel: progressInfo.xpToNextLevel,
        xpProgress: progressInfo.progress,
        currentStreak: effectiveStreak(user),
        longestStreak: user.longest_streak || user.streak_days || 0,
        streakLastDate: user.streak_last_date || null,
        dailyGoal: dailyGoal,
        questionsAnsweredToday: questionsToday,
        dailyGoalCompleted: questionsToday >= dailyGoal,
        earnedAchievements: achievements,
        isLoading: false,
      });
    } catch (error) {
      console.error('Failed to load user stats:', error);
      set({ isLoading: false });
    }
  },

  setDailyGoal: (goal: number) => {
    set({ dailyGoal: goal });
  },

  awardXP: async (userId: string, amount: number, reason: string) => {
    const { xp } = get();
    const newXP = xp + amount;
    const newLevelInfo = calculateLevel(newXP);
    const progressInfo = calculateXPProgress(newXP);

    const previousLevel = get().level;
    const leveledUp = newLevelInfo.level > previousLevel;

    // Update local state immediately for responsive UI
    set({
      xp: newXP,
      level: newLevelInfo.level,
      levelName: newLevelInfo.name,
      levelIcon: newLevelInfo.icon,
      xpToNextLevel: progressInfo.xpToNextLevel,
      xpProgress: progressInfo.progress,
    });

    // Persist to database
    try {
      await db.users.update(userId, {
        xp: newXP,
        level: newLevelInfo.level,
      });
    } catch (error) {
      console.error('Failed to save XP to database:', error);
    }
  },

  recordAnswer: (correct: boolean) => {
    const state = get();
    let newStreak = state.sessionCorrectStreak;

    if (correct) {
      newStreak += 1;
    } else {
      newStreak = 0;
    }

    const newQuestionsToday = state.questionsAnsweredToday + 1;
    const newDailyGoalCompleted = newQuestionsToday >= state.dailyGoal;

    set({
      sessionCorrectStreak: newStreak,
      questionsAnsweredToday: newQuestionsToday,
      dailyGoalCompleted: newDailyGoalCompleted,
    });

    // Note: XP is awarded by Practice.tsx via awardXP() - don't double-award here
  },

  checkAndUpdateStreak: async (userId: string) => {
    const { streakLastDate, currentStreak, longestStreak, dailyGoalCompleted } = get();

    if (!dailyGoalCompleted) return;

    const today = new Date().toISOString();

    if (isNewDay(streakLastDate)) {
      if (isStreakContinued(streakLastDate)) {
        // Continue streak
        const newStreak = currentStreak + 1;
        set({
          currentStreak: newStreak,
          longestStreak: Math.max(longestStreak, newStreak),
          streakLastDate: today,
        });

        // Award streak XP
        await get().awardXP(userId, XP_REWARDS.STREAK_DAY, 'Daily streak');
      } else if (!streakLastDate) {
        // First day of streak
        set({
          currentStreak: 1,
          longestStreak: Math.max(longestStreak, 1),
          streakLastDate: today,
        });
      } else {
        // Streak broken, start new one
        set({
          currentStreak: 1,
          streakLastDate: today,
        });
      }
    }
  },

  checkAchievements: async (user: User) => {
    const { earnedAchievements } = get();
    const earnedIds = new Set(earnedAchievements.map((a) => a.achievement_id));
    const newlyEarned: Achievement[] = [];

    for (const achievement of ACHIEVEMENTS) {
      if (earnedIds.has(achievement.id)) continue;

      let earned = false;

      switch (achievement.trigger_type) {
        case 'questions_answered':
          earned = user.questions_answered >= achievement.trigger_value;
          break;
        case 'streak_days':
          earned = user.streak_days >= achievement.trigger_value;
          break;
        case 'accuracy_50':
          if (user.questions_answered >= 50) {
            const accuracy = (user.questions_correct / user.questions_answered) * 100;
            earned = accuracy >= achievement.trigger_value;
          }
          break;
        case 'level':
          earned = user.level >= achievement.trigger_value;
          break;
        // Add more trigger types as needed
      }

      if (earned) {
        newlyEarned.push(achievement);
      }
    }

    // Save and show newly earned achievements
    const awarded: Achievement[] = [];
    if (newlyEarned.length > 0) {
      for (const achievement of newlyEarned) {
        try {
          // The server checks the criteria and awards the XP (migration 82); users can no
          // longer write their own XP. Legacy client path only until that migration is applied.
          const claimed = await db.achievements.claim(achievement.id);
          if (claimed == null) {
            await db.achievements.award(user.id, achievement.id);
            await get().awardXP(user.id, achievement.xp_reward, `Achievement: ${achievement.name}`);
          } else if (!claimed.awarded) {
            continue; // not earned per the server, or already recorded
          } else {
            await get().loadUserStats(user.id);
          }
          awarded.push(achievement);
          trackEvent('achievement_unlocked', { achievement: achievement.id, name: achievement.name });
          // Fire-and-forget success haptic on iOS — no-op on web.
          void notifyAchievement();
        } catch (error) {
          console.error(`Failed to award achievement ${achievement.id}:`, error);
        }
      }

      // Update local state with the achievements actually awarded
      const newEarnedAchievements: UserAchievement[] = [
        ...earnedAchievements,
        ...awarded.map((a, index) => ({
          id: `${user.id}-${a.id}-${Date.now()}-${index}`,
          user_id: user.id,
          achievement_id: a.id,
          earned_at: new Date().toISOString(),
        })),
      ];
      if (awarded.length > 0) {
        set({
          earnedAchievements: newEarnedAchievements,
          newAchievement: awarded[0],
        });
      }
    }

    return awarded;
  },

  dismissNewAchievement: () => {
    set({ newAchievement: null });
  },

  resetSession: () => {
    set({
      sessionCorrectStreak: 0,
    });
  },
}));

export default useGamificationStore;
